import { Radio, ArrowRight, Server, Code, Container, Cpu } from 'lucide-react';
import { useT } from '@/i18n/provider';
import { cn } from '@/lib/utils';
import { Link } from 'react-router';

const signals = [
  { key: 'logs', icon: Server, color: 'text-info-500', bg: 'bg-info-50 dark:bg-info-500/10', border: 'border-info-200 dark:border-info-500/20' },
  { key: 'traces', icon: Cpu, color: 'text-primary-500', bg: 'bg-primary-50 dark:bg-primary-500/10', border: 'border-primary-200 dark:border-primary-500/20' },
] as const;

const integrations = [
  { key: 'collector', icon: Container },
  { key: 'sdk', icon: Code },
] as const;

export function OtlpSection() {
  const t = useT();

  return (
    <section className="py-24 sm:py-32 gradient-subtle">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left — Text content */}
          <div className="animate-on-scroll">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800/30 mb-6">
              <Radio className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              <span className="text-sm font-medium text-primary-700 dark:text-primary-300">OpenTelemetry</span>
            </div>

            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white text-balance">
              {t('otlpSection.title')}
            </h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
              {t('otlpSection.description')}
            </p>

            {/* Signal cards */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {signals.map((signal, i) => {
                const Icon = signal.icon;
                return (
                  <div
                    key={signal.key}
                    className="animate-on-scroll glass-subtle rounded-xl p-4 flex items-start gap-3"
                    style={{ transitionDelay: `${i * 100 + 100}ms` }}
                  >
                    <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center border shrink-0', signal.bg, signal.border)}>
                      <Icon className={cn('h-5 w-5', signal.color)} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                        {t(`otlpSection.signals.${signal.key}.title`)}
                      </h3>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                        {t(`otlpSection.signals.${signal.key}.description`)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* CTA link */}
            <div className="mt-8 animate-on-scroll" style={{ transitionDelay: '300ms' }}>
              <Link
                to="/docs/integrations/otlp"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors group"
              >
                {t('otlpSection.cta')}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </div>

          {/* Right — Visual: OTLP flow diagram */}
          <div className="animate-on-scroll" style={{ transitionDelay: '200ms' }}>
            <div className="glass-subtle rounded-2xl p-6 sm:p-8 border border-gray-200/60 dark:border-gray-800/60">
              {/* Title bar */}
              <div className="flex items-center gap-2 mb-6">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
                <span className="ml-2 text-xs text-gray-400 dark:text-gray-500 font-mono">otel-collector-config.yaml</span>
              </div>

              {/* Code preview */}
              <pre className="text-xs sm:text-sm font-mono leading-relaxed overflow-x-auto text-gray-300">
                <code>
                  <span className="text-gray-500">{'# OpenTelemetry Collector → LogPulse'}</span>{'\n'}
                  <span className="text-primary-400">exporters</span>:{'\n'}
                  {'  '}<span className="text-info-400">otlphttp/logpulse</span>:{'\n'}
                  {'    '}<span className="text-gray-400">endpoint</span>: <span className="text-success-400">"https://api.logpulse.io"</span>{'\n'}
                  {'    '}<span className="text-gray-400">headers</span>:{'\n'}
                  {'      '}<span className="text-gray-400">Authorization</span>: <span className="text-success-400">"Bearer $API_KEY"</span>{'\n'}
                  {'\n'}
                  <span className="text-primary-400">service</span>:{'\n'}
                  {'  '}<span className="text-gray-400">pipelines</span>:{'\n'}
                  {'    '}<span className="text-info-400">logs</span>:{'\n'}
                  {'      '}<span className="text-gray-400">exporters</span>: [<span className="text-success-400">otlphttp/logpulse</span>]{'\n'}
                  {'    '}<span className="text-primary-400">traces</span>:{'\n'}
                  {'      '}<span className="text-gray-400">exporters</span>: [<span className="text-success-400">otlphttp/logpulse</span>]
                </code>
              </pre>

              {/* Integration badges */}
              <div className="mt-6 pt-5 border-t border-gray-200/60 dark:border-gray-700/30 flex flex-wrap gap-3">
                {integrations.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.key}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/50 text-xs font-medium text-gray-600 dark:text-gray-400"
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {t(`otlpSection.integrations.${item.key}`)}
                    </div>
                  );
                })}
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/50 text-xs font-medium text-gray-600 dark:text-gray-400">
                  <span className="text-success-500 font-bold">K8s</span>
                  {t('otlpSection.integrations.k8s')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
